import React from "react";

interface MatchCardProps {
  p1: string;
  p2: string;
  s1: number | null;
  s2: number | null;
  isLive?: boolean;
  matchNum: string | number;
  isAdmin: boolean;
  isFinalRound?: boolean;
  round?: number;
  blockNumber?: number;
  onResultClick?: (
    round: number,
    blockNumber: number,
    p1Name: string,
    p2Name: string,
    p1Wins: number | null,
    p2Wins: number | null,
  ) => void;
}

const MatchCard: React.FC<MatchCardProps> = ({
  p1,
  p2,
  s1,
  s2,
  isLive,
  matchNum,
  isAdmin,
  isFinalRound = false,
  round = 0,
  blockNumber = 0,
  onResultClick,
}) => {
  const p1Winner = s1 !== null && s2 !== null && s1 > s2;
  const p2Winner = s1 !== null && s2 !== null && s2 > s1;
  const isPending = s1 === null && s2 === null;

  const renderPlayerField = (playerName: string, isWinner: boolean) => {
    return (
      <span
        className={`text-sm truncate pr-2 ${
          isWinner
            ? "font-black text-amber-700"
            : isPending
              ? "text-slate-400"
              : "font-medium text-slate-600"
        }`}
      >
        {playerName}
      </span>
    );
  };

  const renderScoreField = (score: number | null, isWinner: boolean) => {
    return (
      <span
        className={`text-lg font-black ${
          isWinner
            ? "text-amber-600"
            : isPending
              ? "text-slate-300"
              : "text-slate-400"
        }`}
      >
        {score === null ? "-" : score === 999 ? "欠場" : score}
      </span>
    );
  };

  return (
    <div
      onClick={() => {
        if (isAdmin && onResultClick) {
          onResultClick(round, blockNumber, p1, p2, s1, s2);
        }
      }}
      className={`w-56 h-[104px] rounded-xl flex flex-col relative overflow-hidden group shrink-0 transition-all duration-200 ${
        isAdmin ? "cursor-pointer" : ""
      } ${
        isFinalRound
          ? "bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-50 border-2 border-amber-400 final-glow scale-[1.03] hover:scale-[1.05]"
          : "bg-white border border-slate-200 shadow-[0_4px_15px_rgb(0,0,0,0.02)] hover:border-slate-300"
      }`}
    >
      {isFinalRound && (
        <>
          {/* 背景グラデーション */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,213,79,0.4),transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(251,146,60,0.25),transparent_55%)] pointer-events-none" />
          {/* シマーsweep */}
          <div className="absolute inset-y-0 w-[30%] bg-gradient-to-r from-transparent via-white/55 to-transparent final-sweep pointer-events-none" />
          {/* 星々 */}
          <span className="absolute top-1.5 right-2 text-[12px] text-amber-400 final-twinkle pointer-events-none select-none">
            ★
          </span>
          <span
            className="absolute top-2 right-9 text-[7px] text-yellow-400 final-twinkle pointer-events-none select-none"
            style={{ animationDelay: "0.55s" }}
          >
            ✦
          </span>
          <span
            className="absolute bottom-1.5 left-2.5 text-[9px] text-yellow-300 final-twinkle pointer-events-none select-none"
            style={{ animationDelay: "1.1s" }}
          >
            ✧
          </span>
          <span
            className="absolute bottom-2 right-7 text-[10px] text-amber-300 final-twinkle pointer-events-none select-none"
            style={{ animationDelay: "0.3s" }}
          >
            ✦
          </span>
          <span
            className="absolute top-1/2 left-1.5 -translate-y-1/2 text-[7px] text-yellow-200 final-twinkle pointer-events-none select-none"
            style={{ animationDelay: "1.5s" }}
          >
            ★
          </span>
        </>
      )}
      {isLive && (
        <div className="absolute top-0 left-0 w-full h-1 bg-rose-500 animate-pulse" />
      )}
      <div
        className={`absolute top-1 left-2 text-[8px] font-bold uppercase tracking-widest ${
          isFinalRound ? "text-amber-400" : "text-slate-300"
        }`}
      >
        {isFinalRound ? "🏆 FINAL" : `Match ${matchNum}`}
      </div>
      <div
        className={`flex justify-between items-center px-3 pt-4 pb-2 border-b border-slate-100 relative ${
          p1Winner ? "bg-amber-50/70" : ""
        }`}
      >
        {p1Winner && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" />
        )}
        {renderPlayerField(p1, p1Winner)}
        {renderScoreField(s1, p1Winner)}
      </div>
      <div
        className={`flex justify-between items-center px-3 py-2 relative ${
          p2Winner ? "bg-amber-50/70" : ""
        }`}
      >
        {p2Winner && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" />
        )}
        {renderPlayerField(p2, p2Winner)}
        {renderScoreField(s2, p2Winner)}
      </div>
    </div>
  );
};

export default MatchCard;
